import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { safeInvoke } from '../lib/safeInvoke';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';

// ─── Types ────────────────────────────────────────────────────────────
interface LenormandCard {
  id:       number;
  name:     string;
  namePt:   string;
  keywords: string;
  position: string;
}

interface LenormandResult {
  cards:          LenormandCard[];
  interpretation: string;
  spread:         string;
}

type Spread = 'single' | 'three' | 'five' | 'nine';

// ─── Card display ─────────────────────────────────────────────────────
function LenormandCardDisplay({ card, revealed }: { card: LenormandCard; revealed: boolean }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '0.4rem', minWidth: '90px', opacity: revealed ? 1 : 0,
      transition: 'opacity 0.6s ease',
    }}>
      <div style={{
        width: '72px', height: '110px',
        backgroundColor: 'var(--bg-elevated)',
        border: '2px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: '0.3rem', padding: '0.5rem',
      }}>
        <span style={{ fontSize: '1.5rem' }}>🃏</span>
        <p style={{ fontSize: '7px', color: 'var(--text-primary)', textAlign: 'center', margin: 0, fontWeight: 600, lineHeight: 1.3 }}>
          {card.namePt}
        </p>
      </div>
      <p style={{ fontSize: '7px', color: 'var(--text-tertiary)', textAlign: 'center', maxWidth: '80px', margin: 0 }}>
        {card.position}
      </p>
      <p style={{ fontSize: '7px', color: 'var(--petrol)', textAlign: 'center', maxWidth: '80px', margin: 0 }}>
        {card.keywords}
      </p>
    </div>
  );
}

// ─── Main LenormandPage ───────────────────────────────────────────────
const LOADING_PHASES = [
  'Preparando o baralho de Lenormand...',
  'Interpretando as combinações de cartas...',
  'Lendo as mensagens simbólicas...',
  'Compondo sua leitura personalizada...',
];

export function LenormandPage() {
  const { t }       = useI18n();
  const { profile } = useAuth();

  const SPREADS: { key: Spread; label: string; emoji: string; desc: string }[] = [
    { key: 'single', label: t('pages.lenormand.spreadSingle'), emoji: '🃏', desc: t('pages.lenormand.spreadSingleDesc') },
    { key: 'three',  label: t('pages.lenormand.spreadThree'),  emoji: '🎴', desc: t('pages.lenormand.spreadThreeDesc')  },
    { key: 'five',   label: t('pages.lenormand.spreadFive'),   emoji: '✨', desc: t('pages.lenormand.spreadFiveDesc')   },
    { key: 'nine',   label: t('pages.lenormand.spreadNine'),   emoji: '🔮', desc: t('pages.lenormand.spreadNineDesc')   },
  ];

  const [spread,        setSpread]       = useState<Spread | null>(null);
  const [question,      setQuestion]     = useState('');
  const [phase,         setPhase]        = useState<'select' | 'question' | 'loading' | 'done'>('select');
  const [result,        setResult]       = useState<LenormandResult | null>(null);
  const [revealedCount, setRevealedCount] = useState(0);
  const [error,         setError]        = useState<string | null>(null);

  function handleSelectSpread(s: Spread) {
    setSpread(s);
    setPhase('question');
  }

  async function fetchReading() {
    if (!spread) return;
    setPhase('loading');
    setError(null);

    const userName = profile?.full_name?.split(' ')[0] ?? undefined;
    const { data, fromFallback, error: invokeError } = await safeInvoke<LenormandResult>('lenormand-reading', {
      body:     { spread, question: question.trim() || undefined, userName },
      fallback: { cards: [], interpretation: '', spread },
    });

    if (fromFallback || !data.cards?.length) {
      if (invokeError === 'subscription_required') {
        setError(t('pages.lenormand.subscriptionRequired'));
      } else if (invokeError?.includes('AI providers') || invokeError?.includes('quota') || invokeError?.includes('credit')) {
        setError('O oráculo está temporariamente sobrecarregado. Tente novamente em alguns instantes ✦');
      } else {
        setError(invokeError || data.interpretation || 'Erro ao consultar o oráculo. Tente novamente.');
      }
      setPhase('select');
      return;
    }

    setResult(data);
    setRevealedCount(0);
    setPhase('done');

    for (let i = 1; i <= data.cards.length; i++) {
      await new Promise(r => setTimeout(r, 500));
      setRevealedCount(i);
    }
  }

  function restart() {
    setSpread(null);
    setQuestion('');
    setPhase('select');
    setResult(null);
    setRevealedCount(0);
    setError(null);
  }

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
            {t('pages.lenormand.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
            {t('pages.lenormand.subtitle')}
          </p>
        </div>
        {phase !== 'select' && (
          <button className="btn btn-ghost btn-sm" onClick={restart}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <RotateCcw size={15} /> {t('pages.lenormand.restart')}
          </button>
        )}
      </div>

      {error && (
        <div className="card" style={{ borderColor: 'var(--error)', marginBottom: '1rem' }}>
          <p style={{ color: 'var(--error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Spread selection */}
      {phase === 'select' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {SPREADS.map(s => (
            <button key={s.key} className="card" onClick={() => handleSelectSpread(s.key)}
              style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', transition: 'border-color 200ms' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border-gold)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}>
              <span style={{ fontSize: '1.75rem', display: 'block', marginBottom: '0.5rem' }}>{s.emoji}</span>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0 0 0.25rem' }}>{s.label}</p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', margin: 0 }}>{s.desc}</p>
            </button>
          ))}
        </div>
      )}

      {/* Question input */}
      {phase === 'question' && (
        <div className="card" style={{ maxWidth: '560px' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            {t('pages.lenormand.questionLabel')}
          </p>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder={t('pages.lenormand.questionPlaceholder')}
            rows={3}
            style={{
              width: '100%', padding: '0.75rem', resize: 'vertical',
              background: 'var(--bg-input)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
              fontSize: 'var(--text-body)', marginBottom: '1rem',
            }}
          />
          <button className="btn btn-primary" onClick={fetchReading} style={{ width: '100%' }}>
            {t('pages.lenormand.questionCta')}
          </button>
        </div>
      )}

      {/* Loading */}
      {phase === 'loading' && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {/* Results */}
      {phase === 'done' && result && (
        <>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center',
            padding: '1.5rem', backgroundColor: 'var(--bg-surface)',
            borderRadius: 'var(--radius-card)', marginBottom: '1.5rem',
            border: '1px solid var(--border-gold)',
          }}>
            {result.cards.map((card, i) => (
              <LenormandCardDisplay key={i} card={card} revealed={i < revealedCount} />
            ))}
          </div>

          {result.interpretation && revealedCount >= result.cards.length && (
            <div className="card animate-fade-in">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1rem' }}>
                {t('pages.lenormand.readingTitle')}
              </p>
              <div className="reading-text">
                {result.interpretation.split('\n').filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <ReadingActions pageName="Tarot Cigano" reading={result.interpretation} signEmoji="🃏" />
              <ReadingFeedback feature="lenormand-reading" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
